import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { peripheralsSelector } from 'modules/common/Header/selectors';
import { Card, Row, Col, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FieldArray } from 'formik';
import classNames from 'classnames';
import { format } from 'date-fns';
import AlertCircleOutline from 'mdi-react/AlertCircleOutlineIcon';
import InfoCircleOutlineIcon from 'mdi-react/InfoCircleOutlineIcon';

import { convertUTCDateToLocal } from 'utils';
import WeightBtn from 'modules/common/WeightBtn';
import { weigthSelector } from 'modules/common/WeightBtn/selectors';

import { RolesMode } from '../../../entities/Roles';
import { tripStatuses, TripStatusIds } from '../../../entities/Trip';
import { Input, Checkbox, MaskInput } from '../Forms';

import styles from './DepartureData.module.scss';
import { RootState } from '../../../rootReducer';

type Props = {
  mode: RolesMode;
  handleChange: (e: React.ChangeEvent<any>) => void;
  setFieldValue: (name: string, value: any) => void;
  setCallAdminTolerance?: (value: { isWrongTolerance: boolean; tolerance: number }) => void;
  values: any;
  errors: any;
  statusId: TripStatusIds;
  allowWeight: number;
  allowedExitRealTolerance: number;
  allowedExitTheoreticalTolerance: number;
  restStatus?: boolean;
  operatingProcedure: any;
};

type SUM = {
  netto: number;
  package: number;
  brutto: number;
  fvNetto: number;
  tvNetto: number;
};

type ORDER = {
  orderNum: number;
  netWeight: number;
  fvWeight: number;
  tvWeight: number;
  packageWeight: number;
  bruttoWeight: number;
};

const DEFAULT_SUM = {
  netto: 0,
  package: 0,
  brutto: 0,
  fvNetto: 0,
  tvNetto: 0,
} as const;

const DepartureData: React.FC<Props> = ({
  handleChange,
  setFieldValue,
  setCallAdminTolerance,
  values,
  statusId,
  mode,
  allowWeight,
  allowedExitRealTolerance,
  allowedExitTheoreticalTolerance,
  errors,
  restStatus,
  operatingProcedure,
}) => {
  const isSecurity = mode === 'security';
  const isDirector = mode === 'director';

  const [sum, setSum] = useState<SUM>(DEFAULT_SUM);
  const [tolerance, setTolerance] = useState<number>(0);
  const [tolerancePercent, setTolerancePercent] = useState<number>(0);
  const { orders, departureVehicleWeight, entryVehicleWeight, addedPerforationWeight = '0' } = values;
  const isOrders = orders ? orders.length : null;
  const printTolerancePercent = Math.abs(tolerancePercent).toFixed(2);

  // формула макс. можливого відхилення
  const allowedTolerancePercent = useMemo(() => {
    const v =
      sum.tvNetto + sum.fvNetto
        ? (sum.tvNetto * (allowedExitTheoreticalTolerance / 100) + sum.fvNetto * (allowedExitRealTolerance / 100)) *
          (100 / (sum.tvNetto + sum.fvNetto))
        : 0;
    return v;
  }, [allowedExitRealTolerance, allowedExitTheoreticalTolerance, sum.tvNetto, sum.fvNetto]);

  const isWrongTolerance = +printTolerancePercent > allowedTolerancePercent;
  const isWaitingAdminStatus = statusId === tripStatuses.waitingAdmin;
  const isAllowedAdminStatus = statusId === tripStatuses.waitingDeparture;
  const allowedWeight = isWaitingAdminStatus ? 0 : allowWeight;

  // @ts-ignore
  const newError = useSelector((state: RootState) => state.commonReducer.tripFieldsReducer.newError);
  // @ts-ignore
  const load = useSelector((state: RootState) => state.commonReducer.tripFieldsReducer.loading);

  const peripherals = useSelector(peripheralsSelector);
  const isManualGetWeight = peripherals?.find(
    (p) => p.type === 'Scales' && p.processName === 'Exit',
  )?.isManualGetWeight;
  const [isEnableEntryVehicleWeight, setIsEnableEntryVehicleWeight] = useState<boolean | undefined>(isManualGetWeight);

  const changeHandler =
    (name: 'netWeight' | 'packageWeight', index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget;
      setFieldValue(`orders[${index}].${name}`, value);
      setFieldValue(
        `orders[${index}].bruttoWeight`,
        +orders[index][name === 'netWeight' ? 'packageWeight' : 'netWeight'] + +value,
      );
    };

  useEffect(() => {
    if (newError !== null || newError?.data?.ManualAllowed !== undefined) {
      setFieldValue('isManualWeight', true);
    } else {
      setFieldValue('isManualWeight', false);
    }
    console.log();
  }, [newError]);

  const { error } = useSelector(weigthSelector);
  useEffect(() => {
    if (error) {
      setIsEnableEntryVehicleWeight(true);
    }
  }, [error]);

  const isError =
    (errors && errors.orders) ||
    (errors && errors.departureVehicleWeight) ||
    (errors && errors.addedPerforationWeight) ||
    (errors && errors.allowWithTolerance);

  const [factCargoWeight, setFactCargoWeight] = useState(-1);

  useEffect(() => {
    if (departureVehicleWeight) {
      if (setCallAdminTolerance) setCallAdminTolerance({ isWrongTolerance, tolerance });
      let value = 0;
      if (isWaitingAdminStatus && tolerance) {
        value = tolerance;
      } else {
        value = tolerance + Number(allowWeight ?? 0);
      }

      // setFieldValue('allowedWeight', value);
      console.log(value);
      setFieldValue('weightDeviationInKilograms', tolerance);
      setFieldValue('weightDeviationInPercent', tolerancePercent);
    }
  }, [isWrongTolerance, departureVehicleWeight, tolerance]);

  useEffect(() => {
    orders.forEach((o: any, index: number) => {
      if (o.number) {
        setFieldValue(`orders[${index}].bruttoWeight`, +o.netWeight + +o.packageWeight);
      }
    });

    if (restStatus && (!orders || !orders.length)) {
      setFieldValue('isNotLoaded', true);
    }
    if (restStatus && orders && orders.length) {
      setFieldValue('isNotLoaded', false);
    }
  }, [orders]);

  useEffect(() => {
    if (!isOrders) return;

    const data = (orders || []).reduce(
      (acc: SUM, { netWeight, fvWeight, tvWeight, packageWeight }: ORDER) => {
        acc.netto += Number(netWeight ?? 0);
        acc.package += Number(packageWeight ?? 0);
        acc.brutto += Number(netWeight ?? 0) + Number(packageWeight ?? 0);
        acc.fvNetto += Number(fvWeight ?? 0);
        acc.tvNetto += Number(tvWeight ?? 0);

        return acc;
      },
      { ...DEFAULT_SUM },
    );
    setSum(data);

    const calcTolerance =
      departureVehicleWeight -
      Number(addedPerforationWeight ?? 0) -
      Number(allowedWeight ?? 0) -
      entryVehicleWeight -
      data.brutto;

    const calcTolerancePercent = (calcTolerance / data.netto) * 100;

    setTolerance(calcTolerance);
    setTolerancePercent(+calcTolerancePercent.toFixed(2));
  }, [orders, departureVehicleWeight, addedPerforationWeight, entryVehicleWeight]);

  useEffect(() => {
    if (!isOrders) {
      const calcTolerance =
        departureVehicleWeight - Number(addedPerforationWeight ?? 0) - Number(allowedWeight ?? 0) - entryVehicleWeight;
      const calcTolerancePercent = (calcTolerance / entryVehicleWeight) * 100;

      setTolerance(calcTolerance);
      setTolerancePercent(+calcTolerancePercent.toFixed(2));
    }
  }, [isOrders, departureVehicleWeight, entryVehicleWeight, addedPerforationWeight]);

  useEffect(() => {
    setFactCargoWeight(departureVehicleWeight - entryVehicleWeight);
  }, [departureVehicleWeight, entryVehicleWeight]);

  return (
    <Card className={classNames(styles.card, 'mb-3')}>
      <Card.Header className="d-flex py-0">
        {isError && (
          <span className="d-flex align-items-center justify-content-center error-circle">
            <AlertCircleOutline size={15} className="text-danger" />
          </span>
        )}
        <span className="d-block font-weight-bolder ml-4 mr-5 py-1">Дані виїзду</span>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col>
            <FieldArray
              name="orders"
              render={() => (
                <>
                  <Table size="sm" className={styles.table}>
                    <thead>
                      <tr>
                        <th className="d-flex align-items-center">
                          Наряд&nbsp;
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-dec">Якщо наряд відсутній - зверніться на виписку</Tooltip>}
                          >
                            <InfoCircleOutlineIcon size="16" className="text-secondary" />
                          </OverlayTrigger>
                        </th>
                        <th className="pl-5">
                          Вага НЕТТО, кг<em className="text-danger px-1">*</em>
                        </th>
                        <th>
                          Вага упаковки, кг<em className="text-danger px-1">*</em>
                        </th>
                        <th>Вага БРУТТО, кг</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders?.map((value: any, index: number) => (
                        // eslint-disable-next-line
                        <tr key={index}>
                          <td className={classNames('position-relative d-flex align-items-center', styles.cell)}>
                            <Input
                              className="w-100"
                              name={`orders[${index}].number`}
                              label=""
                              onChange={handleChange}
                              requestParamName="number"
                              onLine
                              maxLength={15}
                              isPosNumber
                              disabled={isDirector}
                              readOnly
                              orderInfo={value}
                            />
                          </td>
                          <td className="position-relative pl-5">
                            <Input
                              name={`orders[${index}].netWeight`}
                              label=""
                              onLine
                              disabled={isDirector && newError && !newError?.data?.ManualAllowed}
                              onChange={changeHandler('netWeight', index)}
                              value={values?.orders[index].netWeight}
                              readOnly={newError === null || newError?.data?.ManualAllowed === undefined}
                            />
                          </td>
                          <td className="position-relative">
                            <MaskInput
                              name={`orders[${index}].packageWeight`}
                              label=""
                              onLine
                              placeholder=""
                              maskPlaceholder=""
                              mask="99999999"
                              disabled={isDirector}
                              onChange={changeHandler('packageWeight', index)}
                              value={values?.orders[index].packageWeight}
                            />
                          </td>
                          <td className="d-flex position-relative pt-1">
                            <Input
                              type="number"
                              className={styles.weight}
                              name={`orders[${index}].bruttoWeight`}
                              label=""
                              step="1"
                              pattern="\d+"
                              onLine
                              readOnly
                            />
                            {index === 0 ? (
                              <div className="d-flex">
                                <WeightBtn nameField="ARRAY" type="ARRAY" isDisabled={isDirector} load={load} />
                                {sum.netto !== 0 && sum.fvNetto === 0 && sum.tvNetto === 0 ? (
                                  <span className="text-danger font-weight-bold p-2">!</span>
                                ) : null}
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td>Сума по нарядам, кг:</td>
                        <td className="font-weight-bolder pl-5">{isOrders ? sum?.netto || 0 : ''}</td>
                        <td className="font-weight-bolder">{isOrders ? sum?.package || 0 : ''}</td>
                        <td className="font-weight-bolder">{isOrders ? sum?.brutto || 0 : ''}</td>
                      </tr>
                      {operatingProcedure !== 'Порядок 3' && (
                        <>
                          <tr>
                            <td>Вага автомобіля на в&apos;їзді, кг:</td>
                            <td aria-label="Empty cell" />
                            <td aria-label="Empty cell" />
                            <td className="font-weight-bolder">{entryVehicleWeight}</td>
                          </tr>
                          <tr>
                            <td className="d-flex">
                              Вага перфорації, кг<em className="text-danger px-1">*</em>:
                            </td>
                            <td aria-label="Empty cell" />
                            <td aria-label="Empty cell" />
                            <td className="d-flex position-relative px-1">
                              <MaskInput
                                name="addedPerforationWeight"
                                mask="9999"
                                maskPlaceholder=""
                                disabled={!orders.length || isDirector}
                                onChange={handleChange}
                                value={values?.addedPerforationWeight}
                              />
                              <span className={styles.dotLeftMinus}>*</span>
                            </td>
                          </tr>
                          <tr>
                            <td className="d-flex">
                              Вага автомобіля на виїзді, кг<em className="text-danger px-1">*</em>:
                            </td>
                            <td aria-label="Empty cell" />
                            <td aria-label="Empty cell" />
                            <td className="d-flex position-relative pt-1">
                              <Input
                                type="number"
                                name="departureVehicleWeight"
                                label=""
                                onChange={handleChange}
                                onLine
                                step="1"
                                pattern="\d+"
                                disabled={isDirector || (!isDirector && !isEnableEntryVehicleWeight)}
                                min={0}
                                maxLength={8}
                              />
                              <span className={styles.dot}>*</span>
                              <WeightBtn nameField="departureVehicleWeight" type="OUT" isDisabled={isDirector} />
                            </td>
                          </tr>
                          <tr className={styles.toleranceRow}>
                            <td>Відхилення по автомобілю, кг:</td>
                            <td aria-label="Empty cell" />
                            <td aria-label="Empty cell" />
                            <td
                              className={classNames('font-weight-bolder', {
                                [styles.err]: isWrongTolerance,
                                [styles.ok]: !isWrongTolerance,
                              })}
                            >
                              {!!departureVehicleWeight && (
                                <>
                                  <p className={classNames('d-flex flex-row mb-0', styles.calcCell)}>
                                    {`${tolerance > 0 ? '+' : ''}${tolerance}`}
                                    {Number.isFinite(tolerancePercent) && (
                                      <span className="pl-1">{`(${printTolerancePercent}%)`}</span>
                                    )}
                                  </p>
                                  {isWrongTolerance && (
                                    <p className={classNames('font-weight-bolder', styles.err)}>Толеранс перевищено!</p>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        </>
                      )}
                      {isSecurity && operatingProcedure !== 'Порядок 3' && (
                        <tr>
                          <td className="d-flex">Фактична вага вантажу, кг:</td>
                          <td aria-label="Empty cell" />
                          <td aria-label="Empty cell" />
                          <td>{factCargoWeight > 0 && <b>{factCargoWeight}</b>}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </>
              )}
            />
          </Col>
          <Col xs={4} className="mt-4">
            {(isWaitingAdminStatus || !!allowWeight) && (
              <>
                {values.callDepartmentManagerTime && (
                  <p>{`- Викликано завсклада (${format(
                    convertUTCDateToLocal(values.callDepartmentManagerTime),
                    'HH:mm',
                  )})`}</p>
                )}
                {isAllowedAdminStatus && values.allowWithTolerance && (
                  <p>{`- Завсклад дозволив виїзд (${allowWeight > 0 ? '+' : ''}${allowWeight} кг)`}</p>
                )}
              </>
            )}
          </Col>
        </Row>
        {isDirector ? (
          <Row className="mb-3">
            <Col>
              <div className={styles.lOffset}>
                <Checkbox
                  name="allowWithTolerance"
                  label={`ДОЗВОЛИТИ виїзд з толерансом ${tolerance > 0 ? '+' : ''}${tolerance}`}
                  className="col-4 _req-checkbox"
                  onChange={(e) => {
                    setFieldValue('allowedWeight', tolerance);
                    handleChange(e);
                  }}
                  disabled={isSecurity}
                />
              </div>
            </Col>
          </Row>
        ) : null}
      </Card.Body>
    </Card>
  );
};

export default DepartureData;
